import { currencyExponent, toMinor } from '../domain/money';

/**
 * Turns what a person typed into an amount field into integer minor units.
 *
 * People type the way their keyboard and habits allow, not always the way
 * their locale formats: a French user may type "35.50" or "35,50", an
 * American "1,234.5". The rules, in order:
 *  - both "." and "," present: the last one is the decimal separator
 *  - a single separator followed by 1..exponent digits: decimal
 *  - a separator followed by exactly 3-digit groups: thousands grouping
 *  - currencies without minor units (JPY): every separator is grouping
 * Returns null for empty input and 'invalid' for anything else unreadable.
 */
export type ParsedAmount = number | null | 'invalid';

export function parseAmount(input: string, currency: string): ParsedAmount {
  // Drop spaces, apostrophe grouping (CH) and currency symbols; anything else must be digits/separators.
  const s = input.replace(/[\s  '’]/g, '').replace(/\p{Sc}/gu, '');
  if (s === '') return null;
  if (!/^[\d.,]+$/.test(s) || !/\d/.test(s)) return 'invalid';

  const exp = currencyExponent(currency);
  const lastDot = s.lastIndexOf('.');
  const lastComma = s.lastIndexOf(',');

  let intPart = s;
  let fracPart = '';

  if (lastDot >= 0 && lastComma >= 0) {
    const dec = lastDot > lastComma ? '.' : ',';
    const group = dec === '.' ? ',' : '.';
    const at = s.lastIndexOf(dec);
    intPart = s.slice(0, at);
    fracPart = s.slice(at + 1);
    if (!validGrouping(intPart, group)) return 'invalid';
    intPart = intPart.split(group).join('');
  } else if (lastDot >= 0 || lastComma >= 0) {
    const sep = lastDot >= 0 ? '.' : ',';
    const parts = s.split(sep);
    const tail = parts[parts.length - 1]!;
    const isDecimal = exp > 0 && parts.length === 2 && tail.length >= 1 && tail.length <= exp;
    // "0,500" / ".50": nothing before the separator but zeros, so it can only be a decimal (0.50; trailing zeros dropped).
    const fromZero = exp > 0 && parts.length === 2 && /^0*$/.test(parts[0]!) && tail.length > exp;
    if (isDecimal) {
      intPart = parts[0]!;
      fracPart = tail;
    } else if (fromZero) {
      intPart = '0';
      fracPart = tail.replace(/0+$/, '');
    } else if (validGrouping(s, sep)) {
      intPart = parts.join('');
    } else if (exp > 0 && parts.length === 2 && tail.length === 0) {
      // "35," while typing: treat as "35"
      intPart = parts[0]!;
    } else {
      return 'invalid';
    }
  }

  if (intPart === '') intPart = '0';
  if (!/^\d+$/.test(intPart) || (fracPart !== '' && !/^\d+$/.test(fracPart))) return 'invalid';
  if (fracPart.length > exp) return 'invalid';
  if (intPart.replace(/^0+/, '').length > 12) return 'invalid';

  const major = Number(`${intPart}.${fracPart || '0'}`);
  return toMinor(major, currency);
}

/** "1,234,567" style: first group 1-3 digits (not starting with 0), every later group exactly 3. */
function validGrouping(s: string, sep: string): boolean {
  const groups = s.split(sep);
  if (groups.length === 1) return /^\d+$/.test(s);
  return /^[1-9]\d{0,2}$/.test(groups[0]!) && groups.slice(1).every((g) => /^\d{3}$/.test(g));
}

/** The decimal separator the billing region uses, for the keypad hint and placeholder. */
export function decimalSeparator(formatLocale: string): string {
  return new Intl.NumberFormat(formatLocale).formatToParts(1.5).find((p) => p.type === 'decimal')?.value ?? '.';
}
